import { auth, getActiveAuth } from '../config/firebase';
import { API_BASE_URL } from '../config/api'

// Types for settings
export interface LocationData {
  latitude?: number;
  longitude?: number;
  city?: string;
  country?: string;
  timezone?: string;
  updatedAt?: string;
}

export interface AppearanceSettings {
  theme: 'light' | 'dark' | 'system';
  fontSize: 'small' | 'medium' | 'large' | 'extra_large';
  highContrast: boolean;
  reduceMotion: boolean;
  compactMode: boolean;
  sidebarCollapsed: boolean;
}

export interface NotificationSettings {
  emailNotifications: boolean;
  pushNotifications: boolean;
  smsNotifications: boolean;
  genechatResponses: 'immediate' | 'daily' | 'weekly' | 'monthly' | 'disabled';
  healthReminders: 'immediate' | 'daily' | 'weekly' | 'monthly' | 'disabled';
  reportReady: 'immediate' | 'daily' | 'weekly' | 'monthly' | 'disabled';
  systemUpdates: 'immediate' | 'daily' | 'weekly' | 'monthly' | 'disabled';
  marketingEmails: 'immediate' | 'daily' | 'weekly' | 'monthly' | 'disabled';
  quietHoursEnabled: boolean;
  quietHoursStart?: string;
  quietHoursEnd?: string;
  quietHoursTimezone?: string;
}

export interface PrivacySettings {
  locationSharing: 'enabled' | 'disabled';
  dataRetention: '1_year' | '2_years' | '5_years' | 'indefinite';
  analyticsEnabled: boolean;
  crashReporting: boolean;
  usageStatistics: boolean;
  profilePublic: boolean;
  shareAnonymizedData: boolean;
  allowDataExport: boolean;
  autoDeleteInactive: boolean;
  inactiveThresholdMonths: number;
}

export interface GeneChatSettings {
  personalizationLevel: 'minimal' | 'standard' | 'enhanced';
  proactiveSuggestions: boolean;
  useLocationContext: boolean;
  rememberPreferences: boolean;
  responseLength: 'concise' | 'balanced' | 'detailed';
  includeSources: boolean;
  technicalLevel: 'beginner' | 'intermediate' | 'advanced';
  conversationHistoryLimit: number;
  autoSaveConversations: boolean;
  conversationRetentionDays: number;
}

export interface HealthPreferences {
  measurementSystem: 'metric' | 'imperial';
  language: 'en-US' | 'en-GB' | 'ar-SA' | 'he-IL' | 'pt-PT';
  dietaryRestrictions: string[];
  healthGoals: string[];
  medicalConditions: string[];
  showMedicalDisclaimers: boolean;
  enableHealthReminders: boolean;
}

export interface IntegrationSettings {
  googleFitEnabled: boolean;
  appleHealthEnabled: boolean;
  fitbitEnabled: boolean;
  calendarSyncEnabled: boolean;
  calendarProvider?: 'google' | 'outlook' | 'apple';
  preferredExportFormat: 'pdf' | 'csv' | 'json';
  autoBackupEnabled: boolean;
  backupFrequency: 'daily' | 'weekly' | 'monthly';
}

export interface SocialLinks {
  linkedin?: string;
  twitter?: string;
  instagram?: string;
  facebook?: string;
  youtube?: string;
  website?: string;
}

export interface BrandingSettings {
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  organizationName?: string;
}

export interface ProfessionalProfile {
  bio?: string;
  headline?: string;
  profilePhotoUrl?: string;
  specialties?: string[];
  credentials?: string[];
  affiliations?: string[];
  contactEmail?: string;
  businessName?: string;
  businessAddress?: string;
  serviceMode?: 'online_only' | 'in_person' | 'hybrid';
  languages?: string[];
  socialLinks?: SocialLinks;
  branding?: BrandingSettings;
  // Verification fields
  verificationStatus?: 'pending' | 'approved' | 'rejected' | null;
  verificationDocuments?: string[];
  verificationCredentialDetails?: string;
  verificationSubmittedAt?: string;
  verificationReviewedAt?: string;
  verificationReviewedBy?: string;
  verificationRejectionReason?: string;
  // BAA fields
  baaSignedAt?: string;
}

export interface UserSettings {
  userId: string;
  location: LocationData;
  healthPreferences: HealthPreferences;
  appearance: AppearanceSettings;
  notifications: NotificationSettings;
  privacy: PrivacySettings;
  genechat: GeneChatSettings;
  integrations: IntegrationSettings;
  professionalProfile?: ProfessionalProfile;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface LocationUpdateRequest {
  latitude: number;
  longitude: number;
}

export interface SettingsUpdateRequest {
  appearance?: Partial<AppearanceSettings>;
  notifications?: Partial<NotificationSettings>;
  privacy?: Partial<PrivacySettings>;
  genechat?: Partial<GeneChatSettings>;
  healthPreferences?: Partial<HealthPreferences>;
  integrations?: Partial<IntegrationSettings>;
  professionalProfile?: Partial<ProfessionalProfile>;
}

// API client helper
const getAuthHeaders = async (): Promise<HeadersInit> => {
  const user = getActiveAuth().currentUser
  
  if (!user) {
    throw new Error('User not authenticated')
  }
  
  try {
    const token = await user.getIdToken(true)
    
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }
  } catch (error) {
    console.error('SettingsService: Failed to get ID token', error)
    throw new Error('Failed to get authentication token')
  }
}

const makeAuthenticatedRequest = async (
  url: string,
  options: RequestInit = {},
  retryCount = 0
): Promise<Response> => {
  try {
    const headers = await getAuthHeaders()
    
    const fetchOptions = {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    }

    const response = await fetch(url, fetchOptions)

    if (!response.ok) {
      if (response.status === 401 && retryCount < 1) {
        await new Promise(resolve => setTimeout(resolve, 1000)) // 1s delay
        return makeAuthenticatedRequest(url, options, retryCount + 1)
      }
      const errorData = await response.json().catch(() => ({}))
      console.error('SettingsService: API error:', errorData)
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`)
    }

    return response
  } catch (error) {
    console.error('SettingsService: Authentication request failed:', error)
    throw error
  }
}

class SettingsService {
  private baseUrl = `${API_BASE_URL}/api/v1/settings`;

  /**
   * Get current user settings
   */
  async getUserSettings(): Promise<UserSettings> {
    const response = await makeAuthenticatedRequest(`${this.baseUrl}/`)
    return await response.json()
  }

  /**
   * Update user settings (partial updates supported)
   */
  async updateSettings(updates: SettingsUpdateRequest): Promise<UserSettings> {
    const response = await makeAuthenticatedRequest(`${this.baseUrl}/`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return response.json();
  }

  /**
   * Update user location from browser geolocation
   */
  async updateLocation(location: LocationUpdateRequest): Promise<UserSettings> {
    const response = await makeAuthenticatedRequest(`${this.baseUrl}/location`, {
      method: 'POST',
      body: JSON.stringify(location),
    });
    return response.json();
  }

  /**
   * Clear user location data for privacy
   */
  async clearLocation(): Promise<void> {
    await makeAuthenticatedRequest(`${this.baseUrl}/location`, {
      method: 'DELETE',
    });
  }

  /**
   * Update theme quickly
   */
  async updateTheme(theme: 'light' | 'dark' | 'system'): Promise<void> {
    await makeAuthenticatedRequest(`${this.baseUrl}/theme`, {
      method: 'PUT',
      body: JSON.stringify({ theme }),
    });
  }

  /**
   * Export settings for backup
   */
  async exportSettings(): Promise<any> {
    const response = await makeAuthenticatedRequest(`${this.baseUrl}/export`);
    return response.json();
  }

  /**
   * Request browser geolocation with permission handling
   */
  async requestLocation(): Promise<LocationUpdateRequest> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      const options = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          let errorMessage = 'Unable to retrieve location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied by user';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out';
              break;
          }
          reject(new Error(errorMessage));
        },
        options
      );
    });
  }

  /**
   * Update appearance settings
   */
  async updateAppearance(appearance: Partial<AppearanceSettings>): Promise<UserSettings> {
    return this.updateSettings({ appearance });
  }

  /**
   * Update notification settings
   */
  async updateNotifications(notifications: Partial<NotificationSettings>): Promise<UserSettings> {
    return this.updateSettings({ notifications });
  }

  /**
   * Update privacy settings
   */
  async updatePrivacy(privacy: Partial<PrivacySettings>): Promise<UserSettings> {
    return this.updateSettings({ privacy });
  }

  /**
   * Update GeneChat settings
   */
  async updateGeneChat(genechat: Partial<GeneChatSettings>): Promise<UserSettings> {
    return this.updateSettings({ genechat });
  }

  /**
   * Update health preferences
   */
  async updateHealthPreferences(healthPreferences: Partial<HealthPreferences>): Promise<UserSettings> {
    return this.updateSettings({ healthPreferences });
  }

  /**
   * Update integration settings
   */
  async updateIntegrations(integrations: Partial<IntegrationSettings>): Promise<UserSettings> {
    return this.updateSettings({ integrations });
  }

  /**
   * Update professional profile settings
   */
  async updateProfessionalProfile(professionalProfile: Partial<ProfessionalProfile>): Promise<UserSettings> {
    const payload = { professional_profile: toApiProfessionalProfile(professionalProfile) };
    const response = await makeAuthenticatedRequest(`${this.baseUrl}/`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return response.json();
  }

  /**
   * Apply theme to document
   * Note: This method is kept for backward compatibility.
   * The ThemeContext handles theme management in the new implementation.
   * This method only sets the data-theme attribute for CSS.
   */
  applyTheme(theme: 'light' | 'dark' | 'system') {
    const root = document.documentElement;
    
    if (theme === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      root.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
    } else {
      root.setAttribute('data-theme', theme);
    }
    
    // Note: Chakra color mode should be updated via ThemeContext.setTheme()
    // This method only handles the CSS data-theme attribute
  }

  /**
   * Get system theme preference
   */
  getSystemTheme(): 'light' | 'dark' {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  /**
   * Listen for system theme changes
   */
  onSystemThemeChange(callback: (theme: 'light' | 'dark') => void) {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handler = (e: MediaQueryListEvent) => {
      callback(e.matches ? 'dark' : 'light');
    };
    
    mediaQuery.addEventListener('change', handler);
    
    // Return cleanup function
    return () => mediaQuery.removeEventListener('change', handler);
  }

  /**
   * Download settings as JSON file
   */
  async downloadSettings() {
    try {
      const settings = await this.exportSettings();
      const blob = new Blob([JSON.stringify(settings, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `livewello-settings-${new Date().toISOString().split('T')[0]}.json`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      // Use setTimeout to ensure the click event has been processed before removal
      setTimeout(() => {
        try {
          if (a.parentNode) {
            a.parentNode.removeChild(a);
          }
        } catch {
          // Silently handle any DOM manipulation errors
          console.debug('SettingsService: Link already removed from DOM');
        }
      }, 0);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download settings:', error);
      throw error;
    }
  }

  /**
   * Test authentication headers
   */
  async testAuthHeaders(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await makeAuthenticatedRequest(`${this.baseUrl}test-auth`, {
        method: 'GET',
      });
      
      if (response.ok) {
        const data = await response.json();
        return { success: true, message: data.message || 'Authentication headers working correctly' };
      } else {
        return { success: false, message: 'Authentication headers test failed' };
      }
    } catch (error) {
      return { success: false, message: `Authentication test error: ${error}` };
    }
  }
}

const toApiProfessionalProfile = (profile: Partial<ProfessionalProfile>): Record<string, any> => {
  if (!profile) return {};
  const {
    profilePhotoUrl,
    contactEmail,
    businessName,
    businessAddress,
    serviceMode,
    socialLinks,
    verificationDocuments,
    verificationCredentialDetails,
    verificationSubmittedAt,
    verificationReviewedAt,
    verificationReviewedBy,
    verificationRejectionReason,
    ...rest
  } = profile;

  const toSnake = <T extends Record<string, any>>(obj: T | undefined, mapping: Record<string, string> = {}) => {
    if (!obj) return undefined;
    const out: Record<string, any> = {};
    Object.entries(obj).forEach(([key, value]) => {
      const mapped = mapping[key] || key.replace(/[A-Z]/g, m => `_${m.toLowerCase()}`);
      out[mapped] = value;
    });
    return out;
  };

  const payload: Record<string, any> = {
    ...rest,
    ...(profilePhotoUrl !== undefined ? { profile_photo_url: profilePhotoUrl } : {}),
    ...(contactEmail !== undefined ? { contact_email: contactEmail } : {}),
    ...(businessName !== undefined ? { business_name: businessName } : {}),
    ...(businessAddress !== undefined ? { business_address: businessAddress } : {}),
    ...(serviceMode !== undefined ? { service_mode: serviceMode } : {}),
    ...(socialLinks !== undefined ? { social_links: toSnake(socialLinks) } : {}),
    ...(profile.branding !== undefined ? { branding: toSnake(profile.branding) } : {}),
    ...(verificationDocuments !== undefined ? { verification_documents: verificationDocuments } : {}),
    ...(verificationCredentialDetails !== undefined ? { verification_credential_details: verificationCredentialDetails } : {}),
    ...(verificationSubmittedAt !== undefined ? { verification_submitted_at: verificationSubmittedAt } : {}),
    ...(verificationReviewedAt !== undefined ? { verification_reviewed_at: verificationReviewedAt } : {}),
    ...(verificationReviewedBy !== undefined ? { verification_reviewed_by: verificationReviewedBy } : {}),
    ...(verificationRejectionReason !== undefined ? { verification_rejection_reason: verificationRejectionReason } : {}),
  };

  // Normalize list-like fields if provided as comma-separated strings
  ['languages', 'credentials', 'affiliations', 'specialties'].forEach(key => {
    if ((profile as any)[key] && typeof (profile as any)[key] === 'string') {
      payload[key] = (profile as any)[key]
        .split(',')
        .map((item: string) => item.trim())
        .filter(Boolean);
    }
  });

  return payload;
};

export const settingsService = new SettingsService();
export default settingsService; 
